import React, { useEffect, useMemo, useState } from "react";
import OnboardingScreen, { OnboardingErrorText, OnboardingTitle } from "../OnboardingScreen";
import { StripeService } from "../../../services/stripeService";

export type BillingPeriod = "free" | "month" | "quarter" | "year";

export interface PlanChoice {
  type: "free" | "plus" | "premium";
  name: "Free" | "Plus" | "Premium";
  billingPeriod: BillingPeriod;
  validatedCountry: string;
}

interface PlanStepProps {
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onSelect: (plan: PlanChoice) => void;
}

// Feature lists carried over verbatim from the previous RegisterFlow step 4.
const AI_FEATURES = new Set([
  "Personalised AI Insights & Analysis",
  "The Wizard AI",
  "Custom Insights & Wizard Tone",
]);

const FREE_FEATURES = [
  "1 Wearable Device",
  "Wellness Questionnaire",
  "Training Load & Exercise Logs",
  "Recovery, Niggles & Notes",
  "Planner & Scheduled Sessions",
  "Female Cycle Logging",
  "Last 7 Days Analytics",
];

const PLUS_FEATURES = [
  "Peak Score & Peak Readiness",
  "2 Wearable Devices",
  "Load Balance",
  "Heart Lung Recovery Index (HLR)",
  "Activity & Exercise Analytics",
  "Saved Session Templates",
  "Export Data to CSV",
  "Performance Goals",
  "Custom Dashboard Background",
];

const PREMIUM_FEATURES = [
  "Everything in Plus",
  "3 Wearable Devices",
  "Weekly, Monthly, 12 Week & Custom Date Range Analytics",
  "Personalised AI Insights & Analysis",
  "The Wizard AI",
  "Custom Insights & Wizard Tone",
];

const formatPrice = (price: number, currency: string) => {
  const currencySymbols: Record<string, string> = { USD: "$", EUR: "€", GBP: "£", AUD: "$", CAD: "$", INR: "₹", ZAR: "R" };
  const symbol = currencySymbols[currency] || `${currency} `;
  if ((currency === "INR" || currency === "ZAR") && price >= 1000) return `${symbol}${price.toLocaleString("en-IN")}`;
  return `${symbol}${price.toFixed(2)}`;
};

const formatDuration = (duration: string) => {
  switch (duration) {
    case "quarter": return "3 months";
    case "year": return "year";
    case "month": return "month";
    default: return duration;
  }
};

const perMonth = (price: number, period: BillingPeriod) => (period === "month" ? price : period === "quarter" ? price / 3 : price / 12);
const perWeek = (price: number, period: BillingPeriod) => (period === "month" ? price / 4.33 : period === "quarter" ? price / 13 : price / 52);

const FeatureList: React.FC<{ features: string[] }> = ({ features }) => (
  <div className="mb-5 flex-1">
    <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-base-content/60">Includes</h3>
    <ul className="space-y-1.5">
      {features.map((feature) => {
        const isAI = AI_FEATURES.has(feature);
        return (
          <li key={feature} className="flex items-start gap-2 text-sm text-base-content/90">
            <span className="mt-0.5 flex-shrink-0 text-base-content">{isAI ? "✦" : "✓"}</span>
            <span>{feature}</span>
          </li>
        );
      })}
    </ul>
  </div>
);

const PaidCard: React.FC<{
  title: string;
  tagline: string;
  features: string[];
  price: { price: number; currency: string };
  billingPeriod: BillingPeriod;
  loading: boolean;
  onClick: () => void;
}> = ({ title, tagline, features, price, billingPeriod, loading, onClick }) => (
  <div className="flex flex-1 flex-col rounded-xl border border-peak-border bg-base-100 p-6 lg:p-8">
    <h2 className="mb-1 font-geist text-2xl font-bold text-base-content">{title}</h2>
    <p className="mb-3 font-geist text-sm text-base-content/70">{tagline}</p>
    <div className="mb-1">
      <span className="font-grotesk text-4xl font-bold text-base-content">{formatPrice(price.price, price.currency)}</span>
      <span className="ml-2 text-sm text-base-content/80">/ {formatDuration(billingPeriod)}</span>
    </div>
    <div className="mb-4 mt-2 flex gap-2 text-xs">
      <div className="flex items-center rounded-md border border-base-content/20 bg-base-content/5 px-4 py-2 pl-2 font-geist text-base-content/60">
        <strong>{formatPrice(perMonth(price.price, billingPeriod), price.currency)}</strong>/month
      </div>
      <div className="flex items-center rounded-md border border-base-content/20 bg-base-content/5 px-4 py-2 pl-2 font-geist text-base-content/60">
        <strong>{formatPrice(perWeek(price.price, billingPeriod), price.currency)}</strong>/week
      </div>
    </div>
    <FeatureList features={features} />
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="flex w-full items-center justify-center gap-2 rounded-full bg-lime px-4 py-3 font-grotesk font-bold text-black transition-colors duration-200 hover:bg-lime/80 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {loading ? <span className="loading loading-spinner loading-xs" /> : "Start 14 Day Free Trial"}
    </button>
  </div>
);

/**
 * Final wizard step — the existing Free / Plus / Premium picker from
 * RegisterFlow step 4, unchanged in copy and pricing logic. Country is
 * detected from IP (onboarding no longer collects it).
 */
const PlanStep: React.FC<PlanStepProps> = ({ loading, error, onBack, onSelect }) => {
  const isDark = false;
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("year");
  const [validatedCountry, setValidatedCountry] = useState("United States");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ipCountry = await StripeService.detectCountryFromIp();
        const validated = await StripeService.validateCountryForPricing(ipCountry || undefined);
        if (!cancelled) setValidatedCountry(validated);
      } catch {
        /* fall back to US */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Flat USD pricing (new PEAK account) — same price for every user, no region lookup.
  const peakPeriod = billingPeriod === "year" ? "year" : "month";
  const premiumPrice = useMemo(
    () => ({ price: StripeService.getPeakDisplayPrice("premium", peakPeriod), currency: "USD" }),
    [peakPeriod],
  );
  const plusPrice = useMemo(
    () => ({ price: StripeService.getPeakDisplayPrice("plus", peakPeriod), currency: "USD" }),
    [peakPeriod],
  );

  const choose = (type: PlanChoice["type"], name: PlanChoice["name"]) =>
    onSelect({ type, name, billingPeriod: type === "free" ? "free" : billingPeriod, validatedCountry });

  return (
    <OnboardingScreen onBack={onBack} width="wide">
      <div className="mx-auto w-full max-w-5xl">
        <OnboardingTitle title="Your plan, your training." subtitle="Start free or unlock more from day one." />
        <div className="mt-3"><OnboardingErrorText>{error}</OnboardingErrorText></div>

        <div className="mb-8 mt-6 flex justify-center">
          <fieldset aria-label="Payment frequency" className="w-full max-w-sm">
            <div className={`flex rounded-full p-1 shadow-inner ${isDark ? "bg-white/10" : "bg-base-300"}`}>
              {[
                { value: "free" as const, label: "Free", discount: null },
                { value: "month" as const, label: "Monthly", discount: null },
                { value: "year" as const, label: "Annually", discount: "-17%" },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`relative flex-1 cursor-pointer rounded-full px-1.5 py-2 text-center text-xs font-semibold transition-all duration-200 sm:px-3 sm:text-sm ${
                    billingPeriod === option.value
                      ? isDark ? "bg-white text-black shadow-md" : "bg-base-content text-base-100 shadow-md"
                      : isDark ? "text-white/50 hover:text-white/80" : "text-base-content/50 hover:text-base-content/80"
                  }`}
                >
                  <input
                    type="radio"
                    name="billing-frequency"
                    value={option.value}
                    checked={billingPeriod === option.value}
                    onChange={() => setBillingPeriod(option.value)}
                    className="sr-only"
                  />
                  <span>{option.label}</span>
                  {option.discount && (
                    <span className="absolute -right-2 -top-3 rounded-full bg-lime px-1.5 py-0.5 text-[10px] font-bold text-black sm:inline-flex">{option.discount}</span>
                  )}
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        {billingPeriod === "free" ? (
          <div className="mx-auto max-w-md rounded-xl border border-peak-border bg-base-100 p-6 lg:p-8">
            <h2 className="mb-1 font-geist text-2xl font-bold text-base-content/70">Basic</h2>
            <p className="mb-3 font-geist text-sm text-base-content/70">Start with the basics.</p>
            <div className="mb-1"><span className="font-grotesk text-3xl font-bold text-base-content">Free</span></div>
            <div className="mb-5 inline-block rounded bg-white/20 p-1.5 px-3 text-xs text-base-content"><p>No card required.</p></div>
            <FeatureList features={FREE_FEATURES} />
            <button
              type="button"
              onClick={() => choose("free", "Free")}
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 font-grotesk font-bold text-white transition-colors duration-200 hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <span className="loading loading-spinner loading-xs" /> : "Continue with free account"}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4 md:flex-row lg:gap-6">
            <PaidCard title="Plus" tagline="Train with clarity." features={PLUS_FEATURES} price={plusPrice} billingPeriod={billingPeriod} loading={loading} onClick={() => choose("plus", "Plus")} />
            <PaidCard title="Premium" tagline="Intelligent training tools." features={PREMIUM_FEATURES} price={premiumPrice} billingPeriod={billingPeriod} loading={loading} onClick={() => choose("premium", "Premium")} />
          </div>
        )}
      </div>
    </OnboardingScreen>
  );
};

export default PlanStep;
